import { Link } from '@tanstack/react-router'
import type { FormEvent } from 'react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { getTurnstileSiteKey, isPasswordAuthEnabled } from '@/lib/auth-capabilities'
import { authClient, authSocialProviders } from '@/lib/auth-client'
import {
  AuthCard,
  AuthDivider,
  AuthField,
  AuthSecondaryAction,
  AuthSocialButtons,
  AuthSubmit,
  EmailSentState,
  formatAuthProviderName,
} from './shared'
import { TurnstileWidget, type TurnstileWidgetHandle } from './turnstile-widget'

export function SignUpForm() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null)
  const [emailSent, setEmailSent] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const passwordAuthEnabled = isPasswordAuthEnabled()
  const turnstileSiteKey = getTurnstileSiteKey()
  const captchaEnabled = Boolean(turnstileSiteKey)
  const turnstileRef = useRef<TurnstileWidgetHandle | null>(null)
  const providers = [...authSocialProviders]
  const appOrigin = typeof window !== 'undefined' ? window.location.origin : ''
  const callbackURL = appOrigin ? `${appOrigin}/` : '/'
  const newUserCallbackURL = callbackURL
  const errorCallbackURL = appOrigin ? `${appOrigin}/auth/sign-up` : '/auth/sign-up'

  const getCaptchaFetchOptions = async () => {
    if (!captchaEnabled) return undefined

    try {
      const token = captchaToken ?? (await turnstileRef.current?.execute())
      if (!token) {
        toast.error('Unable to complete the security check')
        return null
      }

      setCaptchaToken(token)
      return {
        headers: {
          'x-captcha-response': token,
        },
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to complete the security check')
      return null
    }
  }

  const refreshCaptcha = () => {
    if (!captchaEnabled) return
    setCaptchaToken(null)
    turnstileRef.current?.reset()
  }

  const handlePasswordSignUp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!passwordAuthEnabled) {
      await handleMagicLinkSignUp()
      return
    }

    if (!password.trim()) {
      toast.error('Password is required')
      return
    }

    const captchaFetchOptions = await getCaptchaFetchOptions()
    if (captchaFetchOptions === null) {
      return
    }

    setLoading(true)
    const result = await authClient.signUp.email({
      name: name.trim() || email.split('@')[0] || 'there',
      email,
      password,
      callbackURL,
      fetchOptions: captchaFetchOptions,
    })
    setLoading(false)
    refreshCaptcha()

    if (result.error) {
      toast.error(result.error.message || 'Unable to create your account')
      return
    }

    toast.success('Account created successfully')
  }

  const handleMagicLinkSignUp = async () => {
    const captchaFetchOptions = await getCaptchaFetchOptions()
    if (captchaFetchOptions === null) {
      return
    }

    setLoading(true)
    const result = await authClient.signIn.magicLink({
      name: name.trim() || email.split('@')[0] || 'there',
      email,
      callbackURL,
      newUserCallbackURL,
      fetchOptions: captchaFetchOptions,
    })
    setLoading(false)
    refreshCaptcha()

    if (result.error) {
      toast.error(result.error.message || 'Unable to send sign-up link')
      return
    }

    toast.success('Check your inbox to finish setup')
    setEmailSent(true)
  }

  const handleSocialSignIn = async (provider: string) => {
    const providerName = formatAuthProviderName(provider)
    const captchaFetchOptions = await getCaptchaFetchOptions()
    if (captchaFetchOptions === null) {
      return
    }

    setLoadingProvider(provider)
    const result = await authClient.signIn.social({
      provider,
      callbackURL,
      newUserCallbackURL,
      errorCallbackURL,
      fetchOptions: captchaFetchOptions,
    })
    setLoadingProvider(null)
    refreshCaptcha()
    if (result.error) {
      toast.error(result.error.message || `Unable to continue with ${providerName}`)
    }
  }

  if (emailSent) {
    return <EmailSentState email={email} onResend={() => setEmailSent(false)} />
  }

  return (
    <AuthCard
      title="Create your route workspace"
      description={
        passwordAuthEnabled
          ? 'Start with the booking link that protects your day.'
          : 'Start free with the booking link that protects your day.'
      }
    >
      <form className="space-y-4" onSubmit={handlePasswordSignUp}>
        <AuthField
          id="sign-up-name"
          label="Name"
          value={name}
          onChange={setName}
          autoComplete="name"
          placeholder="Your name"
        />
        <AuthField
          id="sign-up-email"
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          placeholder="you@business.com"
        />
        {passwordAuthEnabled && (
          <AuthField
            id="sign-up-password"
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            placeholder="Choose a password"
          />
        )}
        {turnstileSiteKey ? (
          <TurnstileWidget
            ref={turnstileRef}
            siteKey={turnstileSiteKey}
            onTokenChange={setCaptchaToken}
          />
        ) : null}
        <AuthSubmit loading={loading || Boolean(loadingProvider)}>
          {passwordAuthEnabled ? 'Create workspace' : 'Continue'}
        </AuthSubmit>
        {passwordAuthEnabled ? (
          <>
            <AuthDivider label="or" />
            <AuthSecondaryAction
              loading={loading}
              disabled={Boolean(loadingProvider)}
              onClick={handleMagicLinkSignUp}
            >
              Send a setup link
            </AuthSecondaryAction>
            <AuthSocialButtons
              providers={providers}
              disabled={loading || Boolean(loadingProvider)}
              loadingProvider={loadingProvider}
              onProvider={handleSocialSignIn}
            />
          </>
        ) : (
          <>
            <AuthDivider />
            <AuthSocialButtons
              providers={providers}
              disabled={loading || Boolean(loadingProvider)}
              loadingProvider={loadingProvider}
              onProvider={handleSocialSignIn}
            />
          </>
        )}
        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link
            className="px-1 font-medium text-foreground underline underline-offset-4 transition-colors hover:text-foreground/80"
            to="/auth/$authView"
            params={{ authView: 'sign-in' }}
          >
            Sign in
          </Link>
        </p>
      </form>
    </AuthCard>
  )
}
